import { AxiosError } from 'axios';
import { DialogProgrammatic } from 'buefy/src/components/dialog';
import { SnackbarProgrammatic } from 'buefy/src/components/snackbar';
import { ModalProgrammatic } from 'buefy/src/components/modal';

const transformCallBacks = (opts: DialogOptions) => {
  const obj = {
    ...opts,
    onConfirm: opts.confirmCallback || opts.onConfirm || null,
    onCancel: opts.cancelCallback || opts.onCancel || null,
  };
  return obj;
};

export const Dialog = DialogProgrammatic;
export const Snackbar = SnackbarProgrammatic;
export const Modal = ModalProgrammatic;

/**
 * Build error message from server API error, including server message and list of API error details.
 */
export const buildServerErrorMessage = (error: Error, prefix?: string): string => {
  const messagePrefix = prefix ?? 'Server API Error';

  const formatPrefix = (value: string) => {
    if (value.length) {
      const updatedValue = value.replace(/:\s?/, '');
      return `${updatedValue}: `;
    }

    return value;
  };

  if (!(error instanceof AxiosError)) {
    const message = (typeof error === 'string') ? error : error?.message;
    if (message) {
      return `<p>${formatPrefix(messagePrefix)}${message}</p>`;
    }

    return messagePrefix;
  }

  const responseJson = error?.response?.data;
  const errorData = responseJson?.error;
  const message = errorData?.message ?? responseJson?.message ?? 'Unknown error';

  const details = errorData?.details;
  const detailValues = details ? Object.values(details).flat() : [];

  let markup = `<p>${formatPrefix(messagePrefix)}${message}</p>`;

  if (detailValues.length) {
    markup += `
      <ul style="padding-left: 1.5rem; list-style: disc;">
        ${detailValues.map((msg) => `<li>${msg}</li>`).join('')}
      </ul>
    `;
  }

  return markup;
};

export function displayModal(modalOpts: ModalOptions): void {
  const modal = new ModalProgrammatic();
  modal.open(modalOpts);
}

export function displayDialog(dialogOpts: DialogOptions): void {
  const dialog = new DialogProgrammatic();
  dialog.confirm(transformCallBacks(dialogOpts));
}

export function displayDialogPrompt(dialogOpts: DialogOptions): void {
  const dialog = new DialogProgrammatic();
  dialog.prompt(transformCallBacks(dialogOpts));
}

export function displayDialogConfirm(dialogOpts: DialogOptions): Promise<{result: any}> {
  if (dialogOpts.promisable) {
    const dialog = new DialogProgrammatic();
    return dialog.confirm(transformCallBacks({
      promisable: true,
      ...transformCallBacks(dialogOpts),
    }));
  }
  const dialog = new DialogProgrammatic();
  return dialog.confirm(transformCallBacks(dialogOpts));
}

export function displayDialogAlert(dialogOpts: DialogOptions): void {
  const dialog = new DialogProgrammatic();
  dialog.alert(transformCallBacks(dialogOpts));
}

export function displaySnackbar(snackbarOpts: SnackbarOptions): any {
  if (!snackbarOpts.position) {
    // eslint-disable-next-line no-param-reassign
    snackbarOpts.position = 'is-bottom';
  }
  const snackbar = new SnackbarProgrammatic();
  return snackbar.open(snackbarOpts);
}

export function displaySnackbarSuccess(snackbarOpts: SnackbarOptions): any {
  if (!snackbarOpts.position) {
    // eslint-disable-next-line no-param-reassign
    snackbarOpts.position = 'is-bottom';
  }
  // eslint-disable-next-line no-param-reassign
  snackbarOpts.type = 'is-success';
  const snackbar = new SnackbarProgrammatic();
  return snackbar.open(snackbarOpts);
}

export function displaySnackbarError(snackbarOpts: SnackbarOptions): any {
  const options = {
    position: 'is-bottom',
    type: 'is-danger',
    duration: 5000,
    ...snackbarOpts,
  };

  const snackbar = new SnackbarProgrammatic();
  return snackbar.open(options);
}

/**
 * Show snackbar with error details from server API response.
 */
export function displayServerError(error: Error, messagePrefix?: string, snackbarOpts?: SnackbarOptions): any {
  const snackbarArgs = {
    message: buildServerErrorMessage(error, messagePrefix),
    ...snackbarOpts,
  };

  displaySnackbarError(snackbarArgs);
}

export default {
  Modal,
  Dialog,
  Snackbar,
  displayModal,
  displayDialogPrompt,
  displayDialog,
  displayDialogAlert,
  displayServerError,
  displaySnackbar,
  displaySnackbarSuccess,
  displaySnackbarError,
  buildServerErrorMessage,
};
